import { legacyTheme, styled } from '@prose-ui/legacy';

import HowToVideo from 'Components/HowToVideo';
import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';

import BlockLayout from 'Blocks/BlockLayout';

import { videosContent } from 'assets/content/landingPages/stylingGel';

import useMedia from 'utils/useMedia';

const StyledBlockLayout = styled(BlockLayout)`
  width: 100vw;

  > div {
    margin: 0 auto;
  }
`;

const HeadingsContainer = styled.div`
  padding: 48px 24px;
  font-size: 28px;
  font-family: ${legacyTheme.typography.fontFamily};
  color: white;
  line-height: 1.2;
  text-transform: uppercase;

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 80px 42px 48px;
  }
`;

const StyledTextTitle = styled(Typography)`
  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 72px;
  }
  ${legacyTheme.breakpoints.up('lg')} {
    display: flex;
    font-size: 36px;

    p {
      padding: 0 18px;
    }
  }
`;

const StyledTextSubtitle = styled(Typography)`
  margin: ${legacyTheme.spacing.s48} 0 0 0;
  font-size: 28px;
  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 36px;
  }
  ${legacyTheme.breakpoints.up('lg')} {
    margin-top: ${legacyTheme.spacing.s16};
  }
`;

const VideosContainer = styled.div`
  width: 100%;

  > div {
    max-width: 400px;
    margin: 0 auto;
    margin-bottom: ${legacyTheme.spacing.s48};
  }

  ${legacyTheme.breakpoints.up('sm')} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    justify-content: center;

    > div {
      width: 330px;
      margin: 0 auto ${legacyTheme.spacing.s72} auto;
    }
  }

  ${legacyTheme.breakpoints.match('lg')} {
    > div:last-child {
      grid-column: span 2;
      justify-self: center;
    }
  }

  ${legacyTheme.breakpoints.up('lg')} {
    grid-auto-rows: 1fr;
    grid-template-columns: repeat(3, 1fr);
    column-gap: ${legacyTheme.spacing.s48};
    margin-bottom: 0;

    > div {
      margin-bottom: ${legacyTheme.spacing.s96};
    }
  }
`;

type StylingGelHowToVideosProps = {
  variant: 'curl' | 'coily' | null;
};

export const StylingGelHowToVideos = ({ variant = null }: StylingGelHowToVideosProps) => {
  const { headlines, subHead, videos, curlVideos, coilyVideos } = videosContent;
  const isDesktop = useMedia(`(min-width: ${breakpoints.md}px)`);
  const activeVideos = variant === 'curl' ? curlVideos : variant === 'coily' ? coilyVideos : videos;

  return (
    // @ts-expect-error BlockLayout is not typed yet
    <StyledBlockLayout backgroundColor="noirDark" contentAlign="center" contentWidth="veryWide">
      <HeadingsContainer>
        <StyledTextTitle markupName="div">
          {headlines.map((headline) => (
            <Typography key={headline} markupName="p">
              {headline}
            </Typography>
          ))}
        </StyledTextTitle>

        <StyledTextSubtitle markupName="p">
          {subHead}
          {isDesktop ? '.' : ':'}
        </StyledTextSubtitle>
      </HeadingsContainer>
      <VideosContainer>
        {activeVideos.map(({ url, howToCaption, productsCaption, image, dataClick }) => (
          <div key={howToCaption}>
            <HowToVideo
              dataClick={dataClick}
              howToCaption={howToCaption}
              image={image}
              productsCaption={productsCaption}
              url={url}
            />
          </div>
        ))}
      </VideosContainer>
    </StyledBlockLayout>
  );
};
